import { createContext, useCallback, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router";
import { APIClientGetProfile, APIClientLogout, APIClientGetPrograms, APIGetCitys, APIClientPushSubscribe, APIClientGetPushSubscriptions, APIGetNewsFeed, APINewsStories } from "./api";
import { needsClientProfile, clientPermissions } from './shared/clientSession';
import { getPushState } from "./shared/pushClient";

import { useClientChatUnread } from './shared/useClientChatUnread';

export const LayoutContext = createContext();

export const OBJECT_TYPES = [
  "Квартира", "Коммерческое", "Частный дом",
  "Офис", "Склад", "Производственное", "Другое",
];

export const DESIRED_TIME_OPTIONS = [
  { value: "within_week",        label: "В ближайшее время" },
  { value: "pick_dates",         label: "Выберу дни в календаре" },
  { value: "specialist_decides", label: "Когда удобно специалисту" },
  { value: "representative", label: "Согласуйте сами с представителем" },
];

const VAPID_PUBLIC_KEY = 'BABDBh5N_h8CYd-FEQaMHI0PiSzJV-axbKBF2dcLA-v6_B2E9_0IXSWryYTEqfF4CNTxJ5o8531JEOLHk77PXpk';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function LayoutContextProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState('');
  const sessionRevision = useRef(0);
  const [permissions, setPermissions] = useState('__all__');
  const [isChildAccount, setIsChildAccount] = useState(false);
  const unreadEnabled = !isChildAccount || permissions === '__all__' || (Array.isArray(permissions) && permissions.includes('view_application'));
  const chatUnread = useClientChatUnread(account, unreadEnabled);

  const [programs, setPrograms] = useState([]);
  const [citys, setCitys] = useState([]);

    const [chatModal, setChatModal] = useState({ open: false, chatId: null, title: '', applicationId: null });
  // Сторис новостей: items — новости со слайдами, полученные с сервера
  const [newsStories, setNewsStories] = useState({ open: false, items: [] });
  const [storiesBar, setStoriesBar] = useState({ items: [], loaded: false });

  const [modalAuth, setModalAuth] = useState(false);
  const [showOrder, setShowOrder] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [showQuickOrder, setShowQuickOrder] = useState(false);
  const [quickOrderCalcAmount, setQuickOrderCalcAmount] = useState(null);

  // ── Push state ──
  const [pwaStatus, setPwaStatus] = useState({
    isInstalled: false,
    showInstallPrompt: false,
    platform: null,
  });
  const [pushPermission, setPushPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  // Реальное состояние: разрешение + наличие подписки в Service Worker.
  // Одного Notification.permission недостаточно — он остаётся 'granted'
  // даже после очистки данных сайта, когда подписки уже нет.
  const [pushState, setPushState] = useState({
    supported: true, permission: 'default', subscribed: false,
    needsPrompt: false, variant: null, checked: false,
  });
  const deferredPromptRef = useRef(null);

  // ── Определение платформы ──
  const getPlatform = useCallback(() => {
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    return 'desktop';
  }, []);

  // ── Проверка статуса уведомлений ──
  const checkNotificationStatus = useCallback(() => {
    if (typeof Notification === 'undefined') return null;
    return {
      supported: 'Notification' in window && 'serviceWorker' in navigator,
      permission: Notification.permission,
    };
  }, []);

  // ── Обновление реального состояния push ──
  const refreshPushState = useCallback(async () => {
    const state = await getPushState();
    setPushState({ ...state, checked: true });
    if (state.permission && state.permission !== 'unsupported') {
      setPushPermission(state.permission);
    }
    return state;
  }, []);

  // ── Подписка на push ──
  const subscribeUserToPush = useCallback(async () => {
    try {
      // 1. Проверки
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        return { type: 'not_supported' };
      }

      // iOS не в PWA
      if (getPlatform() === 'ios') {
        const isStandalone = window.navigator.standalone ||
          window.matchMedia('(display-mode: standalone)').matches;
        if (!isStandalone) {
          return { type: 'ios_need_install' };
        }
      }

      // 2. Запрашиваем разрешение
      const permission = await Notification.requestPermission();
      setPushPermission(permission);

      if (permission === 'denied') {
        return { type: 'permission_denied' };
      }
      if (permission !== 'granted') {
        return { type: 'permission_dismissed' };
      }

      // 3. Получаем SW registration
      const registration = await navigator.serviceWorker.ready;

      // 4. Подписываемся
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const subJSON = subscription.toJSON();

      // 5. Отправляем на сервер
      const result = await APIClientPushSubscribe({
        endpoint: subJSON.endpoint,
        auth_key: subJSON.keys.auth,
        p256dh_key: subJSON.keys.p256dh,
        browser: navigator.userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera)/)?.[0] || 'Unknown',
        platform: getPlatform(),
      });

      if (result?.status === 'subscribed') {
        return { type: 'success' };
      }

      return { type: 'error', detail: result?.detail };
    } catch (err) {
      console.error('Push subscription error:', err);
      return { type: 'error', detail: err.message };
    }
  }, [getPlatform]);

  // ── Регистрация Service Worker ──
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((reg) => {
          console.log('[Client] SW registered:', reg.scope);
        })
        .catch((err) => {
          console.error('[Client] SW registration failed:', err);
        });
    }
  }, []);

  // ── Слушаем сообщения от SW (навигация по клику на push) ──
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handler = (event) => {
      const data = event.data;
      if (data?.type === 'navigate' && data.url) {
        window.location.href = data.url;
      }
      if (data?.type === 'push_received') {
        // Можно обновить счётчик непрочитанных и т.д.
        console.log('[Client] Push received in foreground:', data.data);
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  // ── Сообщаем SW об активности сайта ──
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;

    const sendStatus = () => {
      navigator.serviceWorker.controller?.postMessage({
        type: document.visibilityState === 'visible' ? 'site_active' : 'site_inactive',
        chatId: null, // клиент не использует chatId в URL
      });
    };

    sendStatus();
    document.addEventListener('visibilitychange', sendStatus);
    return () => document.removeEventListener('visibilitychange', sendStatus);
  }, []);

  // ── PWA Install Prompt (Android/Desktop) ──
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setPwaStatus(prev => ({ ...prev, showInstallPrompt: true }));
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Проверяем, уже ли установлено
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone;
    const platform = getPlatform();

    setPwaStatus({
      isInstalled: !!isInstalled,
      showInstallPrompt: !isInstalled && platform !== 'desktop',
      platform,
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [getPlatform]);

  // ── Проверка и восстановление подписки после логина ──
  useEffect(() => {
    if (!account) return;
    if (typeof Notification === 'undefined') return;

    let alive = true;

    (async () => {
      const state = await getPushState();
      if (!alive) return;
      setPushState({ ...state, checked: true });
      setPushPermission(state.permission);

      // Разрешение есть — тихо восстанавливаем подписку.
      // subscribeUserToPush идемпотентен: при granted системный запрос
      // не показывается, а на сервер уходит актуальный endpoint.
      if (state.permission === 'granted') {
        const result = await subscribeUserToPush();
        if (!alive) return;
        if (result?.type === 'success') {
          setPushState(prev => ({ ...prev, subscribed: true, needsPrompt: false, variant: null }));
        }
      }
    })();

    return () => { alive = false; };
  }, [account, subscribeUserToPush]);

  // ── Перепроверка при возврате на вкладку ──
  // Разрешение могли отозвать в настройках браузера, пока сайт был открыт.
  useEffect(() => {
    if (!account) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshPushState();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [account, refreshPushState]);

  // ── Сторис новостей ───────────────────────────────────────────────────────
  // Показываем только в личном кабинете и один раз за сессию браузера:
  // на лендинге сторис о нововведениях сервиса неуместны.
  const location = useLocation();

  const loadNewsFeed = useCallback(async ({ force = false } = {}) => {
    const token = localStorage.getItem('client_token');
    if (!token) return;
    if (!force && sessionStorage.getItem('client_news_feed_checked') === '1') return;

    try {
      const data = await APIGetNewsFeed();
      sessionStorage.setItem('client_news_feed_checked', '1');
      const items = (data?.results || []).filter(n => (n?.slides || []).length > 0);
      if (items.length > 0) {
        setNewsStories({ open: true, items });
      }
    } catch (e) {
      console.warn('[news feed]', e);
    }
  }, []);

  // Лента кружков для шапки: все актуальные истории (включая просмотренные),
  // модалку НЕ открывает — только наполняет кружки.
  const loadStoriesBar = useCallback(async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;
    try {
      const data = await APINewsStories();
      const items = (data?.results || []).filter(n => (n?.slides || []).length > 0);
      setStoriesBar({ items, loaded: true });
    } catch (e) {
      console.warn('[stories bar]', e);
    }
  }, []);

  useEffect(() => {
    if (!account?.id) return;
    if (!location.pathname.startsWith('/lk')) return;
    // Не спорим с открытыми модалками — заявка и чат важнее новостей
    if (modalAuth || showOrder || showQuickOrder || chatModal.open) return;

    const timer = setTimeout(() => { loadNewsFeed(); }, 2000);
    return () => clearTimeout(timer);
  }, [account?.id, location.pathname, modalAuth, showOrder, showQuickOrder, chatModal.open, loadNewsFeed]);

  const restoreSession = useCallback(async () => {
    const revision = ++sessionRevision.current;
    const token = localStorage.getItem('client_token');
    setSessionError('');
    if (!token) { setLoading(false); return; }
    setLoading(true);
    try {
      const profile = await APIClientGetProfile();
      if (revision !== sessionRevision.current || localStorage.getItem('client_token') !== token) return;
      setAccount(profile);
      setPermissions(clientPermissions(profile));
      setIsChildAccount(Boolean(profile.is_child_account));
      if (needsClientProfile(profile)) setModalAuth(true);
    } catch (error) {
      if (revision !== sessionRevision.current) return;
      if (localStorage.getItem('client_token') === token) {
        setSessionError('Не удалось загрузить профиль. Проверьте соединение и повторите попытку.');
      }
    } finally {
      if (revision === sessionRevision.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    restoreSession();
    return () => { sessionRevision.current += 1; };
  }, [restoreSession]);

  useEffect(() => {
    const handle = () => {
      sessionRevision.current += 1;
      setAccount(null);
      setPermissions([]);
      setIsChildAccount(false);
      setSessionError('');
      setLoading(false);
      setModalAuth(true);
    };
    window.addEventListener('client_unauthorized', handle);
    return () => window.removeEventListener('client_unauthorized', handle);
  }, []);

  const login = useCallback((token, userData, isNew = false, perms = [], isChild = false) => {
    sessionRevision.current += 1;
    localStorage.setItem('client_token', token);
    setLoading(false);
    setSessionError('');
    setAccount(userData);
    setPermissions(perms);
    setIsChildAccount(isChild);
    if (needsClientProfile(userData)) { setModalAuth(true); return; }
    setModalAuth(false);
    window.dispatchEvent(new CustomEvent('client_login_success'));
  }, []);

  function getCitys() {
    const token = localStorage.getItem("client_token");
    if (token) {
      APIGetCitys(token).then((data) => {
        if (data?.length) setCitys(data.sort((a, b) => a.name.localeCompare(b.name, 'ru')));
      });
    }
  }

  const completeProfile = useCallback((updatedAccount) => {
    setPermissions(clientPermissions(updatedAccount));
    setIsChildAccount(Boolean(updatedAccount.is_child_account));
    setAccount(updatedAccount);
    setModalAuth(false);
    window.dispatchEvent(new CustomEvent("client_login_success"));
  }, []);

  useEffect(() => {
    APIClientGetPrograms().then((data) => {
      if (Array.isArray(data)) setPrograms(data);
    });
    getCitys();
  }, []);

  useEffect(() => { getCitys(); }, [account]);

  const logout = useCallback(async () => {
    const token = localStorage.getItem('client_token');
    // Запрос отправляется с прежним токеном; локальный выход не ждёт сеть.
    const pending = APIClientLogout();
    sessionRevision.current += 1;
    if (localStorage.getItem('client_token') === token) localStorage.removeItem('client_token');
    setAccount(null);
    setPermissions([]);
    setIsChildAccount(false);
    setSessionError('');
    setLoading(false);
    setModalAuth(false);
    window.dispatchEvent(new CustomEvent('client_logout'));
    await pending;
  }, []);

  return (
    <LayoutContext.Provider value={{
      account, setAccount,
      loading, sessionError, restoreSession,
      permissions, setPermissions, isChildAccount, setIsChildAccount,

      login, logout, completeProfile,

      chatModal, setChatModal, chatUnread,
      newsStories, setNewsStories, loadNewsFeed,
      storiesBar, setStoriesBar, loadStoriesBar,

      modalAuth, setModalAuth,
      showOrder, setShowOrder,
      editOrder, setEditOrder,
      showQuickOrder, setShowQuickOrder,
      quickOrderCalcAmount, setQuickOrderCalcAmount,
      programs,
      citys, setCitys,

      // Push
      pwaStatus,
      pushPermission,
      pushState,
      refreshPushState,
      subscribeUserToPush,
      checkNotificationStatus,
      getPlatform,
    }}>
      {children}
    </LayoutContext.Provider>
  );
}